import { _, helpers, ts } from '_core';

const dollar = (r = 2) =>
  helpers.tabularFormatters.formatTo('float', {
    align: 'right',
    roundDigits: r,
    commasOnThousands: true,
    numericPrefix: '$',
  });

const percent = (r = 2) =>
  helpers.tabularFormatters.formatTo('float', {
    align: 'right',
    roundDigits: r,
    multiplier: 100,
    suffix: '%',
  });

const FIELD_FRIENDLY_NAMES: Record<string, string> = {
  market_cap: 'Market Cap $million',
  adv_traded_pct: 'ADV Traded %',
  specific_risk: 'Specific Risk',
  median_daily_volume_60: 'Median Volume',
  rbics_l1_id: 'Sector Code',
  rbics_l2_id: 'Sub-Sector Code',
  rbics_l3_id: 'Industry Code',
  rbics_l4_id: 'Sub-Industry Code',
  asset_master_linked_assets: 'Linked Assets',
};

export const _getCustomInputName = (key: string) => {
  return FIELD_FRIENDLY_NAMES[key] || _.startCase(key);
};

export default {
  [ts.enums.REPORT_ENUMS.TABLE_SHEET_KEY_ENUM.MAIN]: [
    {
      key: 'period_start',
      name: 'Period Start',
      formatter: helpers.tabularFormatters.formatTo('date', { align: 'right', format: 'YYYY-MM-DD' }),
      filter: 'date',
    },
    {
      key: 'period_end',
      name: 'Period End',
      formatter: helpers.tabularFormatters.formatTo('date', { align: 'right', format: 'YYYY-MM-DD' }),
      filter: 'date',
    },
    {
      key: 'name',
      name: 'Name',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'asset',
      name: 'Asset',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'asset_type',
      name: 'Type',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'ticker',
      name: 'Ticker',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'rbics_l1_id',
      name: FIELD_FRIENDLY_NAMES.rbics_l1_id,
      formatter: helpers.tabularFormatters.formatTo('integer', { align: 'right' }),
      filter: 'string',
    },
    {
      key: 'rbics_l2_id',
      name: FIELD_FRIENDLY_NAMES.rbics_l2_id,
      formatter: helpers.tabularFormatters.formatTo('integer', { align: 'right' }),
      filter: 'string',
    },
    {
      key: 'rbics_l3_id',
      name: FIELD_FRIENDLY_NAMES.rbics_l3_id,
      formatter: helpers.tabularFormatters.formatTo('integer', { align: 'right' }),
      filter: 'string',
    },
    {
      key: 'rbics_l4_id',
      name: FIELD_FRIENDLY_NAMES.rbics_l4_id,
      formatter: helpers.tabularFormatters.formatTo('integer', { align: 'right' }),
      filter: 'string',
    },
    {
      key: 'rbics_l1_name',
      name: 'Sector Name',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'rbics_l2_name',
      name: 'Sub-Sector Name',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'rbics_l3_name',
      name: 'Industry Name',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'rbics_l4_name',
      name: 'Sub-Industry Name',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
    },
    {
      key: 'cusip',
      name: 'Cusip',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
      condition: (features: ts.types.organization.OrganizationFeatures) => !features.hide_cusip_col,
    },
    {
      key: 'isin',
      name: 'ISIN',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'left' }),
      filter: 'string',
      condition: (features: ts.types.organization.OrganizationFeatures) => !features.hide_isin_col,
    },
    {
      key: 'currency',
      name: 'Currency',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'center' }),
      filter: 'string',
    },
    {
      key: 'issuer_country',
      name: 'Issuer Country',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'center' }),
      filter: 'string',
    },
    {
      key: 'quotation_country',
      name: 'Quotation Country',
      formatter: helpers.tabularFormatters.formatTo('string', { align: 'center' }),
      filter: 'string',
    },
    {
      key: 'traded_value',
      name: 'Traded Value',
      formatter: dollar(2),
      filter: 'number',
    },
    {
      key: 'main_initial',
      name: 'Initial Portfolio',
      formatter: percent(3),
      filter: 'number',
    },
    {
      key: 'main_optimal',
      name: 'Optimal Portfolio',
      formatter: percent(3),
      filter: 'number',
    },
    {
      key: 'tcost_weight',
      name: 'T-cost',
      formatter: percent(4),
      filter: 'number',
    },
    {
      key: '%adv',
      name: '% ADV Traded',
      formatter: percent(4),
      description: '% adv of the traded value',
      filter: 'number',
    },
    {
      key: 'volatility',
      name: 'Volatility',
      formatter: percent(4),
      filter: 'number',
    },
    {
      key: 'bid_ask',
      name: 'Bid/Ask',
      formatter: dollar(4),
      filter: 'number',
    },
    {
      key: 'price',
      name: 'Close',
      formatter: dollar(2),
      filter: 'number',
    },
    {
      key: 'volume_usd',
      name: 'Volume',
      formatter: dollar(2),
      filter: 'number',
    },
    {
      key: 'buy_fixed',
      name: 'Buy Fixed',
      formatter: helpers.tabularFormatters.formatTo('float', {
        align: 'right',
        roundDigits: 2,
        commasOnThousands: true,
      }),
      filter: 'number',
    },
    {
      key: 'sell_fixed',
      name: 'Sell Fixed',
      formatter: helpers.tabularFormatters.formatTo('float', {
        align: 'right',
        roundDigits: 2,
        commasOnThousands: true,
      }),
      filter: 'number',
    },
    {
      key: '_breakpoints_', // Dynamic
      name: '%ADV Level Breakpoints',
      formatter: helpers.tabularFormatters.formatTo('float', {
        align: 'right',
        roundDigits: 2,
        commasOnThousands: true,
      }),
      members: (key) =>
        ['0.0', '0.01', '0.02', '0.03', '0.05', '0.07', '0.1', '0.15', '0.2', '0.3', '0.5', '1.0'].includes(key),
      nameFormatter: (key) => key,
      filter: 'inferred',
    },
  ] as ts.types.components.dataGrid.ColumnsData,
};
